const IMPLEMENTATION = 'codex'
const PROJECT_REF = 'tqczsjbroclihyshzmlc'
const WALL_ORIGIN = 'https://matrix-codex.recordbreaker.com.au'
const ALLOWED_BROWSER_ORIGINS = new Set([
  WALL_ORIGIN,
  'https://recordbreaker-matrix-codex.pages.dev',
])
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const RAW_WALL_TOKEN = /^[0-9a-f]{64}$/

export const ACTIVE_WALL_SHARE_STORAGE_KEY = 'recordbreaker-matrix-codex:active-wall-share:v1'

export type BrowserStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

type StoredWallShare = {
  version: 1
  implementation: typeof IMPLEMENTATION
  projectRef: typeof PROJECT_REF
  browserOrigin: string
  ownerId: string
  wallLinkId: string
  url: string
}

export type WallShareIdentity = {
  ownerId: string
  wallLinkId: string
  browserOrigin?: string
  projectRef?: string
}

function defaultStorage(): BrowserStorage | null {
  if (typeof window === 'undefined') return null
  try { return window.localStorage } catch { return null }
}

function currentOrigin(): string {
  return typeof window === 'undefined' ? '' : window.location.origin
}

function normalizeBrowserOrigin(value: string): string | null {
  try {
    const parsed = new URL(value)
    return parsed.origin === value && ALLOWED_BROWSER_ORIGINS.has(parsed.origin) ? parsed.origin : null
  } catch {
    return null
  }
}

export function normalizeWallShareUrl(value: string): string | null {
  try {
    const parsed = new URL(value)
    if (
      parsed.origin !== WALL_ORIGIN ||
      parsed.pathname !== '/wall' ||
      parsed.search !== '' ||
      parsed.username !== '' ||
      parsed.password !== ''
    ) return null
    const match = /^#t=([0-9a-f]{64})$/.exec(parsed.hash)
    const token = match?.[1]
    if (!token || !RAW_WALL_TOKEN.test(token)) return null
    const normalized = `${WALL_ORIGIN}/wall#t=${token}`
    return value === normalized ? normalized : null
  } catch {
    return null
  }
}

function validIdentity(identity: WallShareIdentity): {
  ownerId: string
  wallLinkId: string
  browserOrigin: string
  projectRef: string
} | null {
  const browserOrigin = normalizeBrowserOrigin(identity.browserOrigin ?? currentOrigin())
  const projectRef = identity.projectRef ?? PROJECT_REF
  if (!UUID.test(identity.ownerId) || !UUID.test(identity.wallLinkId) || !browserOrigin || projectRef !== PROJECT_REF) return null
  return { ownerId: identity.ownerId, wallLinkId: identity.wallLinkId, browserOrigin, projectRef }
}

function remove(storage: BrowserStorage | null): void {
  try { storage?.removeItem(ACTIVE_WALL_SHARE_STORAGE_KEY) } catch { /* Browser storage may be unavailable by policy. */ }
}

export function clearActiveWallShareUrl(storage: BrowserStorage | null = defaultStorage()): void {
  remove(storage)
}

export function saveActiveWallShareUrl(
  identity: WallShareIdentity,
  url: string,
  storage: BrowserStorage | null = defaultStorage(),
): string {
  const normalizedIdentity = validIdentity(identity)
  const normalizedUrl = normalizeWallShareUrl(url)
  if (!storage || !normalizedIdentity || !normalizedUrl) {
    remove(storage)
    throw new Error('The active wall URL could not be retained securely on this device.')
  }
  const record: StoredWallShare = {
    version: 1,
    implementation: IMPLEMENTATION,
    projectRef: PROJECT_REF,
    browserOrigin: normalizedIdentity.browserOrigin,
    ownerId: normalizedIdentity.ownerId,
    wallLinkId: normalizedIdentity.wallLinkId,
    url: normalizedUrl,
  }
  try {
    storage.setItem(ACTIVE_WALL_SHARE_STORAGE_KEY, JSON.stringify(record))
  } catch {
    remove(storage)
    throw new Error('The active wall URL could not be retained securely on this device.')
  }
  return normalizedUrl
}

export function replaceActiveWallShareUrl(
  identity: WallShareIdentity,
  url: string,
  storage: BrowserStorage | null = defaultStorage(),
): string {
  remove(storage)
  return saveActiveWallShareUrl(identity, url, storage)
}

export function readActiveWallShareUrl(
  identity: WallShareIdentity,
  storage: BrowserStorage | null = defaultStorage(),
): string | null {
  if (!storage) return null
  const normalizedIdentity = validIdentity(identity)
  if (!normalizedIdentity) {
    remove(storage)
    return null
  }
  let candidate: unknown
  try {
    const raw = storage.getItem(ACTIVE_WALL_SHARE_STORAGE_KEY)
    if (raw === null) return null
    candidate = JSON.parse(raw)
  } catch {
    remove(storage)
    return null
  }
  if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) {
    remove(storage)
    return null
  }
  const record = candidate as Partial<StoredWallShare>
  const normalizedUrl = typeof record.url === 'string' ? normalizeWallShareUrl(record.url) : null
  if (
    record.version !== 1 ||
    record.implementation !== IMPLEMENTATION ||
    record.projectRef !== normalizedIdentity.projectRef ||
    record.browserOrigin !== normalizedIdentity.browserOrigin ||
    record.ownerId !== normalizedIdentity.ownerId ||
    record.wallLinkId !== normalizedIdentity.wallLinkId ||
    !normalizedUrl
  ) {
    remove(storage)
    return null
  }
  return normalizedUrl
}
